import { HttpResponse } from '@angular/common/http';

import { type ZipProgressEvent } from '@shared/types';

import {
  OutcomeDocument,
  OutcomeDownloadEvent,
  OutcomeDownloadFailureReason,
  OutcomeReadyResponse,
  OutcomeStandaloneDocument,
} from '../types';

/**
 * Fixtures for the "Outcomes ready" outcome documents.
 *
 * `outcome-ready.mock.ts` supplies the single happy-path payload. This file
 * covers the acceptance-criteria edge cases that payload cannot express —
 * the empty state, a group large enough to exercise three-rows-then-scroll,
 * a payload with no standalone documents, and the raw download emissions the
 * download flows branch on.
 *
 * @remarks `FileService.downloadFile` swallows transport errors with
 * `catchError(err => of(err))`, so failures arrive on the `next` channel as
 * error-shaped objects rather than as an error notification. The download
 * response fixtures below mirror that shape deliberately — a spec that emits a
 * thrown error instead is not reproducing what the service actually does.
 */

/**
 * @NOTE: DOWNLOAD RESPONSE FIXTURES
 */

/**
 * A successful `downloadOutcomeDocument` emission.
 *
 * @example
 * ```typescript
 * downloadOutcomeDocument: vi.fn(() => of(OUTCOME_DOWNLOAD_SUCCESS_RESPONSE));
 * ```
 */
export const OUTCOME_DOWNLOAD_SUCCESS_RESPONSE = {
  ok: true,
  status: 200,
  body: new ArrayBuffer(0),
} as const;

/**
 * A `downloadOutcomeDocument` emission for a document the user can no longer
 * reach. Drives the "no longer available" toast and the follow-up refetch.
 */
export const OUTCOME_DOWNLOAD_ACCESS_DENIED_RESPONSE = {
  ok: false,
  status: 403,
} as const;

/**
 * A `downloadOutcomeDocument` emission for a document the user has lost access
 * to because it was removed. Exercises the same access-failure branch as a 403.
 */
export const OUTCOME_DOWNLOAD_NOT_FOUND_RESPONSE = {
  ok: false,
  status: 404,
} as const;

/**
 * A `downloadOutcomeDocument` emission for a server-side failure. Drives the
 * general-failure toast rather than the access-failure branch.
 */
export const OUTCOME_DOWNLOAD_SERVER_ERROR_RESPONSE = {
  ok: false,
  status: 500,
} as const;

/**
 * @NOTE: PAYLOAD EDGE CASES
 */

/**
 * Payload for the empty state: the user has no outcome documents in the window.
 *
 * The card must still render its header with a `0 docs · 0 submissions` count
 * badge above the empty state.
 */
export const OUTCOME_READY_EMPTY_MOCK: OutcomeReadyResponse = {
  document_count: 0,
  submission_count: 0,
  submissions: [],
  standalone_documents: [],
};

/**
 * Builds a sequential outcome document for volume fixtures.
 *
 * @param index - Zero-based position, used to derive a stable id and title.
 * @param documentTypeName - Document type driving the row's icon treatment.
 * @returns A fully populated {@link OutcomeDocument}.
 */
function buildOutcomeDocument(
  index: number,
  documentTypeName = 'Approval Letter',
): OutcomeDocument {
  const _sequence = `${index + 1}`.padStart(4, '0');
  const _day = `${(index % 9) + 1}`.padStart(2, '0');

  return {
    id: `d9b7c3a1-${_sequence}-4c22-9d31-7e5f2a4b${_sequence}`,
    title: `Outcome document ${index + 1}`,
    document_type_name: documentTypeName,
    posted_date: `2026-08-${_day}T09:00:00Z`,
  };
}

/**
 * Payload for a high-volume submission group.
 *
 * The AC requires that an expanded group show its first three documents and
 * scroll to reveal the rest, so this fixture carries more than three documents
 * in a single group.
 */
export const OUTCOME_READY_HIGH_VOLUME_MOCK: OutcomeReadyResponse = {
  document_count: 12,
  submission_count: 1,
  submissions: [
    {
      workspace_id: 'c4f1a2b0-0009-4f5a-9c1d-1a2b3c4d0009',
      workspace_name: 'PROTO-2024-401',
      study_name: 'Multi-site cardiology registry',
      sponsor_protocol_id: 'PROTO-2024-401',
      document_count: 12,
      documents: Array.from({ length: 12 }, (_, index) =>
        buildOutcomeDocument(index),
      ),
    },
  ],
  standalone_documents: [],
};

/**
 * Payload with submission groups but no standalone documents.
 *
 * The AC requires the "Individual documents" section be hidden entirely in this
 * case rather than rendering an empty-state message.
 */
export const OUTCOME_READY_NO_STANDALONE_MOCK: OutcomeReadyResponse = {
  document_count: 2,
  submission_count: 1,
  submissions: [
    {
      workspace_id: 'c4f1a2b0-0010-4f5a-9c1d-1a2b3c4d0010',
      workspace_name: 'PROTO-2024-512',
      study_name: 'Paediatric vaccine study',
      sponsor_protocol_id: 'PROTO-2024-512',
      document_count: 2,
      documents: [
        buildOutcomeDocument(0, 'Approval Letter'),
        buildOutcomeDocument(1, 'Consent Form'),
      ],
    },
  ],
  standalone_documents: [],
};

/**
 * Standalone outcome documents covering both icon treatments.
 *
 * @remarks The standalone shape is **unverified against the back end** — the API
 * returned an empty `standalone_documents` array at the time of implementation.
 * Re-confirm the study context fields before relying on this fixture.
 */
export const OUTCOME_STANDALONE_DOCUMENTS_MOCK: OutcomeStandaloneDocument[] = [
  {
    id: 'd1a0e5c2-0011-4a11-8b21-9f0a1b2c0011',
    title: 'Review notes',
    document_type_name: 'Review Notes',
    posted_date: '2026-08-03T13:40:00Z',
    study_name: 'Phase II oncology trial',
    sponsor_protocol_id: 'PROTO-2024-118',
  },
  {
    id: 'd1a0e5c2-0012-4a11-8b21-9f0a1b2c0012',
    title: 'Conditional approval letter',
    document_type_name: 'Conditional Approval Letter',
    posted_date: '2026-08-02T08:15:00Z',
    study_name: 'Dermatology open-label extension',
    sponsor_protocol_id: 'PROTO-2024-207',
  },
  {
    id: 'd1a0e5c2-0013-4a11-8b21-9f0a1b2c0013',
    title:
      'Institutional review board determination letter for protocol amendment seven',
    document_type_name: 'Approval Letter',
    posted_date: '2026-08-01T17:55:00Z',
    study_name:
      'Long-running multinational observational study of chronic conditions',
    sponsor_protocol_id: 'PROTO-2024-333',
  },
];

/**
 * Payload carrying only standalone documents and no submission groups.
 *
 * Exercises the branch where the groups list is empty but the card still has
 * outcomes to render, so the empty state must not appear.
 */
export const OUTCOME_READY_STANDALONE_ONLY_MOCK: OutcomeReadyResponse = {
  document_count: OUTCOME_STANDALONE_DOCUMENTS_MOCK.length,
  submission_count: 0,
  submissions: [],
  standalone_documents: OUTCOME_STANDALONE_DOCUMENTS_MOCK,
};

/**
 * @NOTE: PROGRESS-REPORTING DOWNLOAD EVENTS
 */

/**
 * Builds the event stream a successful progress-reporting download produces.
 *
 * @param percents - Intermediate percentages to emit before completion. Pass an
 * empty array to model a server that never exposed `Content-Length`.
 * @returns Progress events followed by exactly one successful `complete` event.
 *
 * @example
 * ```typescript
 * buildDownloadSuccessEvents([25, 50, 100]);
 * ```
 */
export function buildDownloadSuccessEvents(
  percents: readonly (number | null)[] = [100],
): OutcomeDownloadEvent[] {
  return [
    ...percents.map(
      (percent): OutcomeDownloadEvent => ({ kind: 'progress', percent }),
    ),
    {
      kind: 'complete',
      outcome: {
        status: 'success',
        response:
          OUTCOME_DOWNLOAD_SUCCESS_RESPONSE as unknown as HttpResponse<ArrayBuffer>,
      },
    },
  ];
}

/**
 * Builds the event stream a failed progress-reporting download produces.
 *
 * @param reason - Classified failure reason.
 * @param httpStatus - Status that produced the failure, or null for a transport
 * error that never reached the server.
 * @returns A single failing `complete` event.
 *
 * @example
 * ```typescript
 * buildDownloadFailureEvents('access', 403);
 * ```
 */
export function buildDownloadFailureEvents(
  reason: OutcomeDownloadFailureReason,
  httpStatus: number | null,
): OutcomeDownloadEvent[] {
  return [
    { kind: 'complete', outcome: { status: 'failure', reason, httpStatus } },
  ];
}

/**
 * @NOTE: ZIP GROUP DOWNLOAD EVENTS
 */

/**
 * Builds the event stream the zip worker produces for a group download.
 *
 * @param options - Shape of the completion event, plus optional progress ticks.
 * @returns Progress events followed by exactly one `done` event.
 *
 * @example
 * ```typescript
 * buildZipEvents({ successCount: 3, progress: [[1, 3], [2, 3], [3, 3]] });
 * ```
 */
export function buildZipEvents(options?: {
  successCount?: number;
  failedCount?: number;
  failures?: { title: string; status: number | null }[];
  hasAllFailed?: boolean;
  savedArchive?: boolean;
  progress?: readonly (readonly [number, number])[];
}): ZipProgressEvent[] {
  const _failures = options?.failures ?? [];
  const _failedCount = options?.failedCount ?? _failures.length;

  return [
    ...(options?.progress ?? []).map(
      ([completed, total]): ZipProgressEvent => ({
        type: 'progress',
        completed,
        total,
      }),
    ),
    {
      type: 'done',
      savedArchive: options?.savedArchive ?? !options?.hasAllFailed,
      hasAllFailed: options?.hasAllFailed ?? false,
      successCount: options?.successCount ?? 0,
      failedCount: _failedCount,
      failures: _failures,
    },
  ];
}
